import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Play,
  Square,
  Clock,
  Scan,
  Package,
  CheckCircle,
  AlertCircle,
  Factory,
  Calendar,
  MapPin,
  Plus,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';

// Statuses an operator can clock into. The backend allows clock-in on
// "Released" or "InProgress" (see routers/production.py).
const CLOCKABLE_STATUSES = ['Released', 'InProgress'];

const formatStatus = (s) => {
  if (s === 'InProgress') return 'In Progress';
  return s || '—';
};

const MobileOperatorPage = () => {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeEntry, setActiveEntry] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [todayShift, setTodayShift] = useState(null);
  const [manualInput, setManualInput] = useState('');
  const [scannedItem, setScannedItem] = useState(null);
  const [quickClockWO, setQuickClockWO] = useState('');
  const [elapsedSec, setElapsedSec] = useState(0);

  // Create WO dialog (iter78)
  const [createOpen, setCreateOpen] = useState(false);
  const [boms, setBoms] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [createForm, setCreateForm] = useState({
    bom_id: '',
    target_quantity: 1,
    priority: 5,
    due_date: '',
    notes: '',
    assigned_users: [],
    auto_release: true, // iter78l — default ON
  });

  const canCreateWO = !!user; // iter78l — any logged-in user can create a WO from mobile

  useEffect(() => {
    fetchData();
  }, []);

  // Live elapsed timer for the active session
  useEffect(() => {
    if (!activeEntry?.clock_in) {
      setElapsedSec(0);
      return undefined;
    }
    const startMs = new Date(activeEntry.clock_in).getTime();
    const tick = () => setElapsedSec(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activeEntry]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const activeRes = await api().get('/time-entries/active').catch(() => ({ data: null }));
      setActiveEntry(activeRes.data || null);

      const woRes = await api().get('/work-orders');
      const activeWOs = (woRes.data || []).filter((wo) => CLOCKABLE_STATUSES.includes(wo.status));
      setWorkOrders(activeWOs);

      const shiftRes = await api()
        .get(`/shifts/user/${user?.id}/current`)
        .catch(() => ({ data: null }));
      setTodayShift(shiftRes.data);
    } catch (error) {
      console.error('Failed to fetch data', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCreateRefs = async () => {
    try {
      const [bomsRes, usersRes] = await Promise.all([
        api().get('/boms'),
        api().get('/users/assignable').catch(() => ({ data: [] })),
      ]);
      // Only Released BOMs can back a Work Order
      setBoms((bomsRes.data || []).filter((b) => b.status === 'Released'));
      setAssignableUsers(usersRes.data || []);
    } catch (e) {
      toast.error('Failed to load BOMs / users');
    }
  };

  const openCreate = async () => {
    setCreateForm({
      bom_id: '',
      target_quantity: 1,
      priority: 5,
      due_date: '',
      notes: '',
      assigned_users: [],
      auto_release: true,
    });
    setCreateOpen(true);
    await loadCreateRefs();
  };

  const submitCreate = async () => {
    if (!createForm.bom_id) {
      toast.error('Pick a BOM first');
      return;
    }
    const bom = boms.find((b) => b.id === createForm.bom_id);
    if (!bom) {
      toast.error('Selected BOM is no longer available');
      return;
    }
    const payload = {
      bom_id: bom.id,
      product_type_id: bom.product_type_id,
      target_quantity: 1, // iter78l — locked to 1 (single assembly per WO)
      priority: Math.max(1, Math.min(10, parseInt(createForm.priority, 10) || 5)),
      notes: createForm.notes || null,
      assigned_users: createForm.assigned_users || [],
      due_date: createForm.due_date || null,
      queue_type: 'part_assembly',
    };
    setLoading(true);
    try {
      await api().post(`/work-orders?auto_release=${createForm.auto_release ? 'true' : 'false'}`, payload);
      toast.success(createForm.auto_release ? 'Work order created & released' : 'Work order created');
      setCreateOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setLoading(false);
    }
  };

  // Advance a Released WO to InProgress, then clock in.
  const handleStart = async (workOrder) => {
    setLoading(true);
    try {
      if (workOrder.status === 'Released') {
        await api().put(`/work-orders/${workOrder.id}/status?new_status=InProgress`);
      }
      await api().post('/time-entries/clock-in', { work_order_id: workOrder.id });
      toast.success('Work started — clocked in');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickClockIn = async () => {
    if (!quickClockWO) return;
    const wo = workOrders.find((w) => w.id === quickClockWO);
    if (!wo) return;
    await handleStart(wo);
    setQuickClockWO('');
  };

  const handleClockOut = async () => {
    if (!activeEntry) return;
    setLoading(true);
    try {
      await api().post(`/time-entries/${activeEntry.id}/clock-out`);
      toast.success('Clocked out');
      setActiveEntry(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    } finally {
      setLoading(false);
    }
  };

  // iter78l — One-click finish: mint a finished-unit serial then mark the WO Completed.
  const handleComplete = async (workOrder) => {
    if (!window.confirm(`Mark WO-${workOrder.id.slice(0, 8).toUpperCase()} complete and mint a serial number?`)) return;
    setLoading(true);
    try {
      // iter78p — auto-release Draft/Paused WOs so Complete works in a single click.
      // Surface release errors instead of silently swallowing them.
      if (workOrder.status === 'Draft' || workOrder.status === 'Paused') {
        await api().put(`/work-orders/${workOrder.id}/status?new_status=Released`);
      }
      // If currently clocked-in to this WO, clock out first so labor time is captured.
      if (activeEntry && activeEntry.work_order_id === workOrder.id) {
        await api().post(`/time-entries/${activeEntry.id}/clock-out`).catch(() => {});
      }
      const fu = await api().post('/finished-units', {
        work_order_id: workOrder.id,
        bom_id: workOrder.bom_id,
        product_type_id: workOrder.product_type_id,
      });
      await api().put(`/work-orders/${workOrder.id}/status?new_status=Completed`);
      const serial = fu.data?.serial_number || '(serial assigned)';
      toast.success(`Completed. Serial: ${serial}`);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Complete failed');
    } finally {
      setLoading(false);
    }
  };

  const handleScanOrInput = async () => {
    if (!manualInput.trim()) return;
    setLoading(true);
    try {
      let result = null;
      if (manualInput.startsWith('FU-')) {
        const res = await api().get(`/finished-units/serial/${manualInput}`).catch(() => ({ data: null }));
        if (res.data) result = { type: 'Finished Unit', data: res.data };
      }
      if (!result) {
        const itemsRes = await api().get('/items');
        const item = (itemsRes.data || []).find((i) => i.sku === manualInput);
        if (item) result = { type: 'Item', data: item };
      }
      if (!result && manualInput.startsWith('WO-')) {
        const woRes = await api().get('/work-orders');
        const wo = (woRes.data || []).find((w) => `WO-${w.id.slice(0, 8).toUpperCase()}` === manualInput);
        if (wo) result = { type: 'Work Order', data: wo };
      }
      if (!result) {
        const locsRes = await api().get('/locations');
        const loc = (locsRes.data || []).find((l) => l.bin_id === manualInput);
        if (loc) result = { type: 'Location', data: loc };
      }
      if (result) {
        setScannedItem(result);
        toast.success(`Found: ${result.type}`);
      } else {
        toast.error('No match found for this code');
      }
    } catch (error) {
      toast.error('Scan lookup failed');
    } finally {
      setLoading(false);
      setManualInput('');
    }
  };

  const elapsedLabel = (() => {
    const h = Math.floor(elapsedSec / 3600);
    const m = Math.floor((elapsedSec % 3600) / 60);
    const s = elapsedSec % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  })();

  const toggleAssignee = (uid) => {
    setCreateForm((f) => {
      const has = (f.assigned_users || []).includes(uid);
      return {
        ...f,
        assigned_users: has
          ? f.assigned_users.filter((x) => x !== uid)
          : [...(f.assigned_users || []), uid],
      };
    });
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto pb-8" data-testid="mobile-operator-root">
      {/* Header */}
      <div className="text-center py-4 border-b border-slate-200">
        <h1 className="text-xl font-bold">Operator Dashboard</h1>
        <p className="text-sm text-slate-500">Welcome, {user?.full_name}</p>
      </div>

      {/* Today's Shift */}
      {todayShift && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">{todayShift.shift_name}</p>
                <p className="text-sm text-blue-700">
                  {todayShift.shift_details?.start_time} - {todayShift.shift_details?.end_time}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Session Card with live elapsed timer */}
      <Card className={`${activeEntry ? 'bg-green-50 border-green-300' : 'bg-slate-50'}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className={`w-5 h-5 ${activeEntry ? 'text-green-600' : 'text-slate-400'}`} />
            Current Session
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeEntry ? (
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-sm text-slate-500">Work Order</p>
                    <p className="font-bold">WO-{activeEntry.work_order_id?.slice(0, 8).toUpperCase()}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-slate-500">Started</p>
                    <p className="font-medium">{new Date(activeEntry.clock_in).toLocaleTimeString()}</p>
                  </div>
                </div>
                <div
                  className="mt-3 flex items-center justify-center gap-2 rounded-md bg-green-100 py-3 font-mono text-3xl font-bold tracking-widest text-green-800"
                  data-testid="mobile-elapsed-timer"
                >
                  <Zap className="w-5 h-5" />
                  {elapsedLabel}
                </div>
              </div>
              <Button
                onClick={handleClockOut}
                disabled={loading}
                className="w-full h-14 bg-red-500 hover:bg-red-600 text-white text-lg"
                data-testid="clock-out-btn"
              >
                <Square className="w-5 h-5 mr-2" />
                Clock Out
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center py-4 text-slate-500">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Not currently clocked in</p>
              </div>

              {/* Quick Clock In */}
              <div className="space-y-2">
                <Label>Quick Start a Work Order</Label>
                <Select
                  value={quickClockWO || 'none'}
                  onValueChange={(v) => setQuickClockWO(v === 'none' ? '' : v)}
                >
                  <SelectTrigger className="h-12" data-testid="quick-wo-select">
                    <SelectValue placeholder="Select Work Order" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select Work Order</SelectItem>
                    {workOrders.map((wo) => (
                      <SelectItem key={wo.id} value={wo.id}>
                        WO-{wo.id.slice(0, 8).toUpperCase()} — {formatStatus(wo.status)} ({wo.target_quantity}u)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {workOrders.length === 0 && (
                  <p className="text-xs text-slate-500">
                    No released or in-progress work orders.{' '}
                    {canCreateWO ? 'Tap “New Work Order” to create one.' : 'Ask a supervisor to release a WO.'}
                  </p>
                )}
                <Button
                  onClick={handleQuickClockIn}
                  disabled={loading || !quickClockWO}
                  className="w-full h-14 bg-green-600 hover:bg-green-700 text-white text-lg"
                  data-testid="clock-in-btn"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Start &amp; Clock In
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create new WO */}
      {canCreateWO && (
        <Button
          onClick={openCreate}
          className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="mobile-create-wo-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Work Order
        </Button>
      )}

      {/* Scanner / Manual Entry */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Scan className="w-5 h-5 text-indigo-600" />
            Scan / Lookup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="Enter SKU, Serial, WO#, or Bin ID"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handleScanOrInput()}
              className="flex-1 h-12"
              data-testid="scan-input"
            />
            <Button
              onClick={handleScanOrInput}
              disabled={loading || !manualInput.trim()}
              className="h-12 px-6"
              data-testid="lookup-btn"
            >
              <Scan className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-xs text-slate-500 text-center">
            Scan QR code or manually enter item SKU, serial number, work order, or bin ID
          </p>
        </CardContent>
      </Card>

      {/* Scanned Item Result */}
      {scannedItem && (
        <Card className="border-indigo-200 bg-indigo-50">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              {scannedItem.type === 'Item' && <Package className="w-5 h-5 text-indigo-600" />}
              {scannedItem.type === 'Finished Unit' && <CheckCircle className="w-5 h-5 text-green-600" />}
              {scannedItem.type === 'Work Order' && <Factory className="w-5 h-5 text-orange-600" />}
              {scannedItem.type === 'Location' && <MapPin className="w-5 h-5 text-blue-600" />}
              {scannedItem.type}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setScannedItem(null)}>
              ✕
            </Button>
          </CardHeader>
          <CardContent>
            {scannedItem.type === 'Item' && (
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-slate-500">SKU:</span><span className="font-mono font-bold">{scannedItem.data.sku}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Name:</span><span className="font-medium">{scannedItem.data.name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Category:</span><span>{scannedItem.data.category}</span></div>
              </div>
            )}
            {scannedItem.type === 'Finished Unit' && (
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-slate-500">Serial:</span><span className="font-mono font-bold">{scannedItem.data.serial_number}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Status:</span><span>{scannedItem.data.status}</span></div>
              </div>
            )}
            {scannedItem.type === 'Work Order' && (
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-slate-500">WO#:</span><span className="font-mono font-bold">WO-{scannedItem.data.id.slice(0, 8).toUpperCase()}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Status:</span><span>{formatStatus(scannedItem.data.status)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Target:</span><span>{scannedItem.data.target_quantity} units</span></div>
                {!activeEntry && CLOCKABLE_STATUSES.includes(scannedItem.data.status) && (
                  <Button
                    onClick={() => handleStart(scannedItem.data)}
                    disabled={loading}
                    className="w-full mt-2 bg-green-600 hover:bg-green-700"
                    data-testid="scan-clock-in-btn"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start &amp; Clock In to This WO
                  </Button>
                )}
              </div>
            )}
            {scannedItem.type === 'Location' && (
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-slate-500">Name:</span><span className="font-bold">{scannedItem.data.name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Bin ID:</span><span className="font-mono">{scannedItem.data.bin_id}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Type:</span><span>{scannedItem.data.type}</span></div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Available Work Orders */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Factory className="w-5 h-5 text-orange-600" />
            Available Work Orders
          </CardTitle>
        </CardHeader>
        <CardContent>
          {workOrders.length > 0 ? (
            <div className="space-y-2" data-testid="mobile-wo-list">
              {workOrders.slice(0, 10).map((wo) => (
                <div
                  key={wo.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                >
                  <div>
                    <p className="font-mono font-bold">WO-{wo.id.slice(0, 8).toUpperCase()}</p>
                    <p className="text-sm text-slate-500">
                      {wo.target_quantity} unit • {formatStatus(wo.status)}
                      {wo.due_date ? ` • due ${wo.due_date}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!activeEntry && (
                      <Button
                        size="sm"
                        onClick={() => handleStart(wo)}
                        disabled={loading}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`mobile-wo-start-${wo.id}`}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Start
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleComplete(wo)}
                      disabled={loading}
                      className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                      data-testid={`mobile-wo-complete-${wo.id}`}
                      title="Mark complete & mint serial"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Done
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-slate-500">
              <Factory className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No active work orders</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button variant="outline" className="h-14" onClick={fetchData} disabled={loading}>
          Refresh Data
        </Button>
        <Button variant="outline" className="h-14" onClick={() => (window.location.href = '/')}>
          Full Dashboard
        </Button>
      </div>

      {/* Create Work Order Dialog (iter78) */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="mobile-create-wo-dialog">
          <DialogHeader>
            <DialogTitle>New Work Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>BOM (Released only)</Label>
              <Select
                value={createForm.bom_id || 'none'}
                onValueChange={(v) => setCreateForm((f) => ({ ...f, bom_id: v === 'none' ? '' : v }))}
              >
                <SelectTrigger data-testid="mobile-create-bom-select"><SelectValue placeholder="Pick a BOM" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Select —</SelectItem>
                  {boms.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.bom_number || b.name || b.id.slice(0, 8)} — {b.product_name || b.product_type_name || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {boms.length === 0 && (
                <p className="text-xs text-slate-500 mt-1">No Released BOMs found. Release one on desktop first.</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Priority (1-10)</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={createForm.priority}
                  onChange={(e) => setCreateForm((f) => ({ ...f, priority: e.target.value }))}
                  data-testid="mobile-create-priority"
                />
              </div>
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={createForm.due_date}
                  onChange={(e) => setCreateForm((f) => ({ ...f, due_date: e.target.value }))}
                  data-testid="mobile-create-due"
                />
              </div>
            </div>

            {/* iter78l — Auto-release toggle (default ON) */}
            <label className="flex items-center gap-2 text-sm select-none">
              <input
                type="checkbox"
                checked={createForm.auto_release}
                onChange={(e) => setCreateForm((f) => ({ ...f, auto_release: e.target.checked }))}
                data-testid="mobile-create-auto-release"
                className="h-4 w-4"
              />
              Release immediately (operator can clock in right away)
            </label>

            <div>
              <Label>Assigned Operators</Label>
              <div className="max-h-32 overflow-y-auto rounded-md border border-slate-200 p-1">
                {assignableUsers.length === 0 && (
                  <p className="text-xs text-slate-500 p-2">No users loaded.</p>
                )}
                {assignableUsers.map((u) => {
                  const checked = createForm.assigned_users.includes(u.id);
                  return (
                    <button
                      type="button"
                      key={u.id}
                      onClick={() => toggleAssignee(u.id)}
                      className={`w-full text-left text-sm px-2 py-1.5 rounded flex items-center justify-between ${
                        checked ? 'bg-orange-50 text-orange-700' : 'hover:bg-slate-50'
                      }`}
                      data-testid={`mobile-create-assign-${u.id}`}
                    >
                      <span>{u.full_name || u.email}</span>
                      <span className="text-xs text-slate-400">{u.role}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                rows={2}
                value={createForm.notes}
                onChange={(e) => setCreateForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Optional"
                data-testid="mobile-create-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={submitCreate}
              disabled={loading || !createForm.bom_id}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="mobile-create-submit"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileOperatorPage;
